import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SEL, uploadFile } from "@/components/employes/shared";
import { fmtDate } from "@/lib/format";

const VIDE = { numero: "", date: "", tiers: "", marchandise: "", valeur_douane: "", droits_douane: "", montant_ht: "", tva: "",
  quittance_numero: "", quittance_date: "", entree_stock: "", d10_id: "", observations: "", type_frais: "" };
const NUM = ["valeur_douane", "droits_douane", "montant_ht", "tva"];
export const TYPES_FRAIS = ["Fret maritime", "Assurance", "Port / EPAL / manutention", "Magasinage", "Transport", "Frais bancaires de domiciliation", "Contrôle qualité", "Autres frais"];
const TITRES = { d10: "une D10", transitaire: "une facture transitaire", frais: "une facture de frais d'importation" };

const useOptions = (sid, d10) => {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!sid) { setOpts([]); return; }
    const url = d10 ? `/douane/entrees-stock?societe_id=${sid}` : `/douane?kind=d10&societe_id=${sid}`;
    api.get(url).then((r) => setOpts(r.data)).catch(() => setOpts([]));
  }, [sid, d10]);
  return opts;
};

export const DouaneDialog = ({ kind, doc, onClose, onSaved }) => {
  const { societes, societeId } = useApp();
  const d10 = kind === "d10";
  const frais = kind === "frais";
  const [v, setV] = useState({ ...VIDE, societe_id: societeId !== "all" ? societeId : "", ...(frais && { type_frais: TYPES_FRAIS[0] }), ...doc });
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false);
  const opts = useOptions(v.societe_id, d10);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const save = async () => {
    if (!v.societe_id || !v.numero || !v.date) return toast.error("Société, numéro et date obligatoires");
    setSaving(true);
    try {
      const f1 = await uploadFile(files.doc);
      const f2 = await uploadFile(files.quittance);
      const body = { ...v, kind, ...f1, ...(f2.file_id && { quittance_file_id: f2.file_id, quittance_file_nom: f2.file_nom }),
        quittance_date: v.quittance_date || null, entree_stock: v.entree_stock || null, d10_id: v.d10_id || null };
      NUM.forEach((k) => { body[k] = Number(v[k]) || 0; });
      await (doc?.id ? api.put(`/douane/${doc.id}`, body) : api.post("/douane", body));
      toast.success("Enregistré — TVA reprise dans le G50");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const field = (k, l, type = "text") => (
    <label className="text-xs text-muted-foreground space-y-1">{l}
      <Input type={type} value={v[k] ?? ""} onChange={set(k)} data-testid={`douane-input-${k}`} />
    </label>
  );
  const fileField = (k, l) => (
    <label className="text-xs text-muted-foreground space-y-1">{l}
      <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles({ ...files, [k]: e.target.files[0] })} data-testid={`douane-file-${k}`} />
    </label>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="douane-dialog">
        <DialogHeader><DialogTitle>{doc?.id ? "Modifier" : "Ajouter"} {TITRES[kind]}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-muted-foreground space-y-1 col-span-2">Société
            <select className={SEL} value={v.societe_id} onChange={set("societe_id")} data-testid="douane-input-societe">
              <option value="">— Choisir —</option>
              {societes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          {field("numero", d10 ? "N° D10" : "N° facture")}
          {field("date", d10 ? "Date D10" : "Date facture", "date")}
          {field("tiers", d10 ? "Fournisseur étranger" : frais ? "Fournisseur" : "Transitaire")}
          {frais && <label className="text-xs text-muted-foreground space-y-1">Type de frais
            <select className={SEL} value={v.type_frais} onChange={set("type_frais")} data-testid="douane-input-type_frais">
              {TYPES_FRAIS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>}
          {field("marchandise", "Marchandise")}
          {d10 ? <>{field("valeur_douane", "Valeur en douane (DA)", "number")}{field("droits_douane", "Droits de douane (DA)", "number")}</>
            : field("montant_ht", "Montant HT (DA)", "number")}
          {field("tva", d10 ? "TVA payée à la douane (DA)" : "TVA (DA)", "number")}
          {d10 && <>{field("quittance_numero", "N° quittance (reçu)")}{field("quittance_date", "Date quittance", "date")}</>}
          <label className="text-xs text-muted-foreground space-y-1 col-span-2">{d10 ? "Entrée de stock (matière première)" : "D10 concernée"}
            <select className={SEL} value={(d10 ? v.entree_stock : v.d10_id) || ""} onChange={set(d10 ? "entree_stock" : "d10_id")} data-testid="douane-input-lien">
              <option value="">— Aucune —</option>
              {opts.map((o) => d10
                ? <option key={o.key} value={o.key}>{fmtDate(o.date)} — {o.designation} ({o.quantite} {o.unite})</option>
                : <option key={o.id} value={o.id}>D10 {o.numero} — {fmtDate(o.date)} {o.marchandise}</option>)}
            </select>
          </label>
          {fileField("doc", d10 ? "PDF / photo de la D10" : "PDF / photo de la facture")}
          {d10 && fileField("quittance", "PDF / photo du reçu")}
          <div className="col-span-2">{field("observations", "Observations")}</div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="douane-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="douane-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
